import React, { useCallback, useEffect, useState } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../../firebase';

// ─── Types ───────────────────────────────────────────────────────────────────

interface StepHistoryEntry {
  stepId: string;
  type?: string;
  status: 'success' | 'failed' | 'skipped' | 'waiting';
  executedAt?: Timestamp;
  error?: string;
  messageId?: string;
  tag?: string;
  variant?: string;
  result?: boolean;
  reason?: string;
}

interface Execution {
  id: string;
  contactId: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled' | 'waiting';
  startedAt?: Timestamp;
  completedAt?: Timestamp;
  error?: string;
  stepHistory: StepHistoryEntry[];
  context?: { contactData?: { email?: string; firstName?: string; lastName?: string } };
}

interface Props {
  automationId: string;
  workspaceId?: string;
  onExecutionsChange?: (executions: Execution[]) => void;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const STATUS_STYLES: Record<string, string> = {
  completed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  running:   'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  pending:   'bg-slate-100 text-slate-500 dark:bg-neutral-800 dark:text-neutral-400',
  waiting:   'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  failed:    'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  cancelled: 'bg-slate-100 text-slate-500 dark:bg-neutral-800 dark:text-neutral-400',
};

const STEP_DOT: Record<string, string> = {
  success: 'bg-emerald-500',
  failed:  'bg-red-500',
  skipped: 'bg-slate-300 dark:bg-neutral-600',
  waiting: 'bg-amber-400',
};

function fmtTs(ts?: Timestamp): string {
  if (!ts) return '—';
  return ts.toDate().toLocaleString('en-US', {
    month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function fmtDuration(start?: Timestamp, end?: Timestamp): string | null {
  if (!start || !end) return null;
  const ms = end.toMillis() - start.toMillis();
  if (ms < 0) return null;
  if (ms < 60_000) return `${Math.round(ms / 1000)}s`;
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)}m`;
  return `${Math.round(ms / 3_600_000)}h`;
}

function contactLabel(exec: Execution): string {
  const d = exec.context?.contactData;
  if (!d) return exec.contactId.slice(0, 8) + '…';
  const name = [d.firstName, d.lastName].filter(Boolean).join(' ');
  return name || d.email || exec.contactId.slice(0, 8) + '…';
}

function stepLabel(entry: StepHistoryEntry): string {
  switch (entry.type) {
    case 'send_email':    return 'Send email';
    case 'add_tag':       return `Add tag${entry.tag ? ` "${entry.tag}"` : ''}`;
    case 'remove_tag':    return `Remove tag${entry.tag ? ` "${entry.tag}"` : ''}`;
    case 'update_contact': return 'Update contact';
    case 'add_to_segment': return 'Add to segment';
    case 'remove_from_segment': return 'Remove from segment';
    case 'update_lead_score': return 'Update lead score';
    case 'track_goal':    return 'Track goal';
    case 'track_conversion': return 'Track conversion';
    case 'webhook':       return 'Webhook';
    case 'delay':         return entry.status === 'waiting' ? 'Waiting (delay)' : 'Delay';
    case 'condition':     return `Condition → ${entry.result ? 'true' : 'false'}`;
    case 'split':         return `Split → ${entry.result ? 'yes' : 'no'}`;
    case 'ab_test':       return `A/B test${entry.variant ? ` (${entry.variant})` : ''}`;
    default:              return entry.type || entry.stepId;
  }
}

// ─── Component ───────────────────────────────────────────────────────────────

const AutomationActivityLog: React.FC<Props> = ({ automationId, workspaceId, onExecutionsChange }) => {
  const [executions, setExecutions] = useState<Execution[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'failed' | 'running' | 'completed'>('all');

  useEffect(() => {
    if (!automationId) return;

    setLoading(true);
    const q = query(
      collection(db, 'automationExecutions'),
      where('automationId', '==', automationId),
      orderBy('startedAt', 'desc'),
      limit(50)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() } as Execution));
        setExecutions(rows);
        onExecutionsChange?.(rows);
        setLoading(false);
      },
      (err) => {
        console.error('[AutomationActivityLog] Firestore error:', err);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [automationId]);

  const toggle = useCallback(
    (id: string) => setExpandedId((prev) => (prev === id ? null : id)),
    []
  );

  const visible = filter === 'all'
    ? executions
    : executions.filter((e) => e.status === filter);

  // ── Summary counts ──────────────────────────────────────────────────────
  const total     = executions.length;
  const completed = executions.filter((e) => e.status === 'completed').length;
  const failed    = executions.filter((e) => e.status === 'failed').length;
  const active    = executions.filter((e) => e.status === 'running' || e.status === 'waiting').length;

  return (
    <div className="flex flex-col h-full bg-white dark:bg-neutral-950 overflow-hidden">

      {/* Header */}
      <div className="flex-shrink-0 px-6 py-4 border-b border-slate-200 dark:border-neutral-800">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-[15px] font-semibold text-slate-900 dark:text-white">Activity log</h2>
            <p className="text-xs text-slate-500 dark:text-neutral-500 mt-0.5">
              Last 50 executions · live
            </p>
          </div>
          {/* Live indicator */}
          <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-emerald-600 dark:text-emerald-400">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            Live
          </span>
        </div>

        {/* Summary tiles */}
        <div className="grid grid-cols-4 gap-2 mb-4">
          {[
            { label: 'Total',     value: total,     color: 'text-slate-700 dark:text-neutral-300' },
            { label: 'Completed', value: completed, color: 'text-emerald-600 dark:text-emerald-400' },
            { label: 'Active',    value: active,    color: 'text-blue-600 dark:text-blue-400' },
            { label: 'Failed',    value: failed,    color: 'text-red-500 dark:text-red-400' },
          ].map((s) => (
            <div key={s.label} className="rounded-lg bg-slate-50 dark:bg-neutral-900 px-3 py-2 text-center">
              <p className={`text-lg font-semibold tabular-nums ${s.color}`}>{s.value}</p>
              <p className="text-[10px] font-medium text-slate-400 dark:text-neutral-600 uppercase tracking-wide">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Filter pills */}
        <div className="flex gap-1.5">
          {(['all', 'running', 'completed', 'failed'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1 rounded-full text-[12px] font-medium transition-colors ${
                filter === f
                  ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900'
                  : 'bg-slate-100 dark:bg-neutral-800 text-slate-500 dark:text-neutral-400 hover:bg-slate-200 dark:hover:bg-neutral-700'
              }`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex flex-col gap-2 p-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-14 rounded-xl bg-slate-100 dark:bg-neutral-800 animate-pulse" />
            ))}
          </div>
        ) : visible.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full py-20 text-center px-6">
            <div className="w-12 h-12 rounded-2xl bg-slate-100 dark:bg-neutral-800 flex items-center justify-center mb-4">
              <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <p className="text-[15px] font-medium text-slate-900 dark:text-white mb-1">
              {filter === 'all' ? 'No executions yet' : `No ${filter} executions`}
            </p>
            <p className="text-sm text-slate-400 dark:text-neutral-500 max-w-xs">
              {filter === 'all'
                ? 'Activate the automation and enroll a contact to see activity here.'
                : `Switch to "All" to see the full history.`}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100 dark:divide-neutral-800/80">
            {visible.map((exec) => {
              const isExpanded = expandedId === exec.id;
              const duration = fmtDuration(exec.startedAt, exec.completedAt);
              return (
                <div key={exec.id}>
                  {/* Row */}
                  <button
                    className="w-full text-left px-5 py-3.5 hover:bg-slate-50 dark:hover:bg-neutral-900/60 transition-colors flex items-center gap-3"
                    onClick={() => toggle(exec.id)}
                  >
                    {/* Status badge */}
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide flex-shrink-0 ${STATUS_STYLES[exec.status] || STATUS_STYLES.pending}`}>
                      {exec.status}
                    </span>

                    {/* Contact */}
                    <span className="text-sm font-medium text-slate-800 dark:text-neutral-200 flex-1 truncate">
                      {contactLabel(exec)}
                    </span>

                    {/* Steps count */}
                    <span className="text-[11px] text-slate-400 dark:text-neutral-600 flex-shrink-0 hidden sm:block">
                      {exec.stepHistory?.length ?? 0} step{exec.stepHistory?.length !== 1 ? 's' : ''}
                    </span>

                    {/* Duration */}
                    {duration && (
                      <span className="text-[11px] text-slate-400 dark:text-neutral-600 flex-shrink-0">
                        {duration}
                      </span>
                    )}

                    {/* Timestamp */}
                    <span className="text-[11px] text-slate-400 dark:text-neutral-600 flex-shrink-0 hidden md:block">
                      {fmtTs(exec.startedAt)}
                    </span>

                    {/* Chevron */}
                    <svg
                      className={`w-4 h-4 text-slate-300 dark:text-neutral-600 flex-shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                      fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {/* Expanded detail */}
                  {isExpanded && (
                    <div className="px-5 pb-4 pt-1 bg-slate-50/50 dark:bg-neutral-900/30">
                      {/* Error */}
                      {exec.error && (
                        <div className="mb-3 rounded-lg border border-red-200 dark:border-red-800/60 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-xs text-red-700 dark:text-red-400">
                          {exec.error}
                        </div>
                      )}

                      {/* Contact detail row */}
                      <div className="flex gap-4 mb-3 text-[11px] text-slate-500 dark:text-neutral-500">
                        <span>Contact: <span className="font-medium text-slate-700 dark:text-neutral-300">{exec.context?.contactData?.email || exec.contactId}</span></span>
                        {exec.completedAt && <span>Completed: <span className="font-medium text-slate-700 dark:text-neutral-300">{fmtTs(exec.completedAt)}</span></span>}
                      </div>

                      {/* Step timeline */}
                      {exec.stepHistory && exec.stepHistory.length > 0 ? (
                        <ol className="relative pl-4 space-y-2 border-l border-slate-200 dark:border-neutral-700">
                          {exec.stepHistory.map((entry, idx) => (
                            <li key={idx} className="relative flex items-start gap-3 text-xs">
                              {/* Dot */}
                              <span
                                className={`absolute -left-[5px] top-1 w-2 h-2 rounded-full flex-shrink-0 ${STEP_DOT[entry.status] || 'bg-slate-300'}`}
                              />
                              <div className="min-w-0">
                                <span className={`font-medium ${entry.status === 'failed' ? 'text-red-600 dark:text-red-400' : 'text-slate-700 dark:text-neutral-300'}`}>
                                  {stepLabel(entry)}
                                </span>
                                {entry.status === 'skipped' && entry.reason && (
                                  <span className="ml-1.5 text-slate-400 dark:text-neutral-600">({entry.reason})</span>
                                )}
                                {entry.messageId && (
                                  <span className="ml-1.5 text-slate-400 dark:text-neutral-600">· ID {entry.messageId}</span>
                                )}
                                {entry.error && (
                                  <p className="mt-0.5 text-red-500 dark:text-red-400 text-[10px]">{entry.error}</p>
                                )}
                              </div>
                              <span className="ml-auto flex-shrink-0 text-[10px] text-slate-400 dark:text-neutral-600">
                                {fmtTs(entry.executedAt)}
                              </span>
                            </li>
                          ))}
                        </ol>
                      ) : (
                        <p className="text-[11px] text-slate-400 dark:text-neutral-600 italic">No step history recorded.</p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AutomationActivityLog;
